import useTranslation from 'next-translate/useTranslation'
import { FC } from 'react'
import { FlexboxGrid } from 'rsuite'

import DiscountPercentageTag from './DiscountPercentageTag'
import { formatGenericIntegerNumber, formatPriceNumber } from '../../utils/math'
import { Product } from '../../utils/types/Product'
import usePrice from '../../services/usePrice'
import { libraryFunctions } from '../../lib/resources/price'
import { getPerBoxItems, getPerCartonItems, getPricePerPieceUnit, getSalesUnit, isPairProduct } from '../../lib/salesUnit'
import NameTag from '../CustomPricing/NameTag'
import { useAuth } from '../../services/useAuth'

import styles from '../../styles/DiscountTableRow.module.less'

const { getProductMoq } = libraryFunctions

interface DiscountTableRowProps {
  product: Product
  discountTier: number
}

const DiscountTableRow: FC<DiscountTableRowProps> = ({ product, discountTier }) => {
  const { t } = useTranslation('products')
  const { user } = useAuth()

  const perPackage = getPerBoxItems(product)
  const perCarton = getPerCartonItems(product)
  const productPricingUnit = getPricePerPieceUnit(product)
  const moq = getProductMoq(product)
  const salesUnit = getSalesUnit(product)
  const totalQuantity = moq * discountTier

  const {
    default: {
      price: defaultPrice,
    },
    final: {
      price: finalPrice,
      reference: referencePrice,
    },
    currency,
    hasCustomPrices,
  } = usePrice(product, totalQuantity)

  const unit = isPairProduct(product) ? 'pair_other' : 'pc_other'

  const renderSalesBreakdown = () => {
    const translatedSalesUnit = t(salesUnit, { count: 2 })

    if (salesUnit === 'carton') {
      return (
        <FlexboxGrid
          align="middle"
          className={styles['moq-section']}
        >
          <FlexboxGrid.Item>{`${totalQuantity} ${translatedSalesUnit}`}</FlexboxGrid.Item>
          <FlexboxGrid.Item className={styles['piece-text']}>
            {`(${formatGenericIntegerNumber(totalQuantity * perCarton * perPackage)} ${t(unit)})`}
          </FlexboxGrid.Item>
        </FlexboxGrid>
      )
    }

    if (salesUnit === 'package') {
      return (
        <FlexboxGrid
          align="middle"
          className={styles['moq-section']}
        >
          <FlexboxGrid.Item>{`${totalQuantity} ${translatedSalesUnit}`}</FlexboxGrid.Item>
          <FlexboxGrid.Item className={styles['piece-text']}>
            {`(${formatGenericIntegerNumber(totalQuantity * perPackage)} ${t(unit)})`}
          </FlexboxGrid.Item>
        </FlexboxGrid>
      )
    }

    return `${totalQuantity} ${translatedSalesUnit}`
  }

  return (
    <FlexboxGrid
      justify="space-between"
      className={styles.row}
      align="middle"
    >
      <FlexboxGrid.Item>
        <FlexboxGrid align="middle">
          {hasCustomPrices && (
            <NameTag
              name={user?.company ?? ''}
              customStyle="margin-right-spacer-half"
            />
          )}
          {renderSalesBreakdown()}
        </FlexboxGrid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item>
        <FlexboxGrid className={styles['price-section']}>
          <DiscountPercentageTag
            referencePrice={finalPrice}
            fullPrice={defaultPrice}
            currency={currency}
            showOnlyDiscount
          />
          <p className={styles['reference-price']}>
            <span>
              {formatPriceNumber(referencePrice)}
            </span>
        &nbsp;
            {currency}
            {' / '}
            {t(productPricingUnit, { count: 1 })}
          </p>
        </FlexboxGrid>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  )
}

export default DiscountTableRow
